/**
 * Tutorials (2026-10-27): the practice gate. Small on purpose: it ships with
 * the shell, so a lesson that was running before a reload is fenced off from
 * the server BEFORE the page under it asks for anything.
 *
 * While a lesson runs, `window.fetch` is wrapped. For every `/api` request:
 *   1. the lesson's practice answers what it owns (its practice records);
 *   2. a read it does not own goes to the server as usual (lists, settings);
 *   3. a write it does not own NEVER leaves the browser: it is answered with
 *      a refusal that says it is practice, and recorded.
 * The only writes let through are the session's own plumbing (sign-in and
 * out, presence, notices read) and the lesson's own progress.
 *
 * State lives in sessionStorage: a reload resumes the lesson, a new tab does
 * not inherit it. The lesson book itself loads lazily (`./lessons`).
 */
import type { Lesson, PracticeRequest, PracticeStore, PracticeWrite } from './types';

const ACTIVE_KEY = 'amassa:tutorial:active';
const STORE_KEY = 'amassa:tutorial:practice';

export interface ActiveLesson {
  lessonId: string;
  step: number;
  /** Epoch ms: anything the screen fetched after this may hold practice records. */
  startedAt: number;
}

/** What a refused write answers: plain words, a status the screens show as an error. */
export const PRACTICE_REFUSAL = 'Practice lesson: nothing was saved. End the lesson to work on real data.';
export const PRACTICE_REFUSAL_STATUS = 409;

/** Writes that are the session's own plumbing, never a change to the company's data. */
const PASS_WRITES: RegExp[] = [
  /^\/auth\//,
  /^\/presence\//,
  /^\/employeeportal\/notifications(\/|$)/,
  /^\/onboarding\/me\/lessons(\/|$)/,
];

// ── The running lesson ───────────────────────────────────────────────

const listeners = new Set<() => void>();
let cached: ActiveLesson | null | undefined;

function emit(): void {
  for (const l of listeners) l();
}

export function subscribeActive(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readActive(): ActiveLesson | null {
  if (cached !== undefined) return cached;
  cached = null;
  try {
    const raw = storage()?.getItem(ACTIVE_KEY);
    const v = raw ? (JSON.parse(raw) as Partial<ActiveLesson>) : null;
    if (v && typeof v.lessonId === 'string' && Number.isInteger(v.step) && (v.step as number) >= 0) {
      cached = { lessonId: v.lessonId, step: v.step as number, startedAt: Number(v.startedAt) || Date.now() };
    }
  } catch {
    /* unreadable: no lesson */
  }
  return cached;
}

export function setActive(next: ActiveLesson | null): void {
  cached = next;
  try {
    if (next) storage()?.setItem(ACTIVE_KEY, JSON.stringify(next));
    else storage()?.removeItem(ACTIVE_KEY);
  } catch {
    /* the in-memory copy still drives this tab */
  }
  if (next) installGate();
  else uninstallGate();
  emit();
}

/** Move the running lesson to another step (no-op when none runs). */
export function setActiveStep(step: number): void {
  const a = readActive();
  if (a && a.step !== step) setActive({ ...a, step });
}

// ── The practice records ─────────────────────────────────────────────

let store: (PracticeStore & { lessonId: string }) | null = null;

export function readStore(lessonId: string): PracticeStore {
  if (store && store.lessonId === lessonId) return store;
  try {
    const raw = storage()?.getItem(STORE_KEY);
    const v = raw ? (JSON.parse(raw) as { lessonId?: string; data?: Record<string, unknown>; writes?: PracticeWrite[] }) : null;
    if (v && v.lessonId === lessonId && v.data && typeof v.data === 'object') {
      store = { lessonId, data: v.data, writes: Array.isArray(v.writes) ? v.writes : [] };
      return store;
    }
  } catch {
    /* fall through to empty */
  }
  store = { lessonId, data: {}, writes: [] };
  return store;
}

export function resetStore(lessonId: string, data: Record<string, unknown>): PracticeStore {
  store = { lessonId, data, writes: [] };
  saveStore();
  return store;
}

export function saveStore(): void {
  if (!store) return;
  try {
    storage()?.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    /* in memory only */
  }
}

export function clearStore(): void {
  store = null;
  try {
    storage()?.removeItem(STORE_KEY);
  } catch {
    /* nothing */
  }
}

/** Something in the practice store changed (a write was answered): the runner re-reads. */
const storeListeners = new Set<() => void>();
export function subscribeStore(listener: () => void): () => void {
  storeListeners.add(listener);
  return () => {
    storeListeners.delete(listener);
  };
}
function storeChanged(): void {
  saveStore();
  for (const l of storeListeners) l();
}

// ── The fetch wrap ───────────────────────────────────────────────────

type LessonLoader = (id: string) => Promise<Lesson | undefined>;
let loader: LessonLoader = (id) => import('./lessons').then((m) => m.lessonById(id));

/** Tests: hand the gate a lesson book without the dynamic import. */
export function setLessonLoader(next: LessonLoader | null): void {
  loader = next ?? ((id) => import('./lessons').then((m) => m.lessonById(id)));
}

let realFetch: typeof fetch | null = null;

function apiPath(input: RequestInfo | URL): { path: string; query: URLSearchParams } | null {
  let href: string;
  if (typeof input === 'string') href = input;
  else if (input instanceof URL) href = input.href;
  else href = input.url;
  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  if (!url.pathname.startsWith('/api/')) return null;
  return { path: url.pathname.slice(4), query: url.searchParams };
}

function readBody(body: BodyInit | null | undefined): unknown {
  if (body == null) return null;
  if (typeof body !== 'string') return null;
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
}

function json(status: number, body: unknown): Response {
  return new Response(JSON.stringify(body), { status, headers: { 'content-type': 'application/json' } });
}

async function practiceFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const pass = realFetch ?? fetch;
  const active = readActive();
  const target = typeof window === 'undefined' ? null : apiPath(input);
  if (!active || !target) return pass(input, init);

  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  const isRead = method === 'GET' || method === 'HEAD';
  const req: PracticeRequest = { method, path: target.path, query: target.query, body: readBody(init?.body) };

  let lesson: Lesson | undefined;
  try {
    lesson = await loader(active.lessonId);
  } catch {
    lesson = undefined;
  }
  const s = readStore(active.lessonId);
  const answer = lesson ? lesson.practice.respond(req, s) : null;

  if (answer) {
    if (!isRead) {
      s.writes.push({ method, path: target.path, body: req.body, answered: true, at: Date.now() });
    }
    storeChanged();
    return json(answer.status ?? 200, answer.body);
  }
  if (isRead) return pass(input, init);
  if (PASS_WRITES.some((re) => re.test(target.path))) return pass(input, init);

  s.writes.push({ method, path: target.path, body: req.body, answered: false, at: Date.now() });
  storeChanged();
  return json(PRACTICE_REFUSAL_STATUS, { ok: false, error: PRACTICE_REFUSAL, practice: true });
}

export function installGate(): void {
  if (typeof window === 'undefined' || realFetch) return;
  realFetch = window.fetch;
  window.fetch = practiceFetch as typeof fetch;
}

export function uninstallGate(): void {
  if (typeof window === 'undefined' || !realFetch) return;
  // Only unwrap our own wrap: something else (a test stub) may sit on top.
  if (window.fetch === (practiceFetch as typeof fetch)) window.fetch = realFetch;
  realFetch = null;
}

/** Is the gate in place right now? (the ribbon and the tests read it) */
export function gateInstalled(): boolean {
  return realFetch !== null;
}

/** Tests: forget everything held in memory. */
export function __resetGateForTests(): void {
  uninstallGate();
  cached = undefined;
  store = null;
  listeners.clear();
  storeListeners.clear();
}

// A lesson that was running before a reload: fence the server off at once,
// before the page under it fetches anything.
if (readActive()) installGate();
